<template>
  <div class="budget-report">
    <Manpower_bar />

    <v-container fluid class="pa-6">
      <!-- Header Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="header-section">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                <h1 class="page-title">
                  <v-icon large color="white" class="mr-3">mdi-office-building</v-icon>
                  ข้อมูลประเภทสถานศึกษา
                </h1>
                <p class="page-subtitle mt-2">
                  ระบบจัดการข้อมูลงบประมาณและประเภทสถานศึกษา สังกัด สอศ.
                </p>
              </div>
              <div class="d-flex align-center header-badges">
                <v-chip class="mr-3 header-chip" dark>
                  <v-icon left small>mdi-calendar</v-icon>
                  ปีงบประมาณ {{ getCurrentYear() }}
                </v-chip>
                <v-chip class="header-chip" color="white" outlined>
                  <v-icon left small color="white">mdi-domain</v-icon>
                  <span class="white--text">{{ budget_etc.length }} รายการ</span>
                </v-chip>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Statistics Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-blue" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">รวมงบประมาณ</p>
                  <h2 class="stat-value">{{ formatNumber(statistics.totalBudget) }}</h2>
                  <p class="stat-percent">รายการ</p>
                </div>
                <v-avatar size="56" color="blue" class="stat-icon">
                  <v-icon dark size="28">mdi-currency-usd</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="100" 
                color="blue" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-green" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">สถานศึกษาปลอดภัย</p>
                  <h2 class="stat-value">{{ formatNumber(statistics.safetyCount) }}</h2>
                  <p class="stat-percent">สถานศึกษา</p>
                </div>
                <v-avatar size="56" color="green" class="stat-icon">
                  <v-icon dark size="28">mdi-shield-check</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="statistics.safetyPercent" 
                color="green" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-orange" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">ประเภทงบประมาณ</p>
                  <h2 class="stat-value">{{ formatNumber(statistics.budgetTypes) }}</h2>
                  <p class="stat-percent">ประเภท</p>
                </div>
                <v-avatar size="56" color="orange" class="stat-icon">
                  <v-icon dark size="28">mdi-tag-multiple</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="100" 
                color="orange" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-purple" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">รวมจำนวน</p>
                  <h2 class="stat-value">{{ formatNumber(statistics.totalCount) }}</h2>
                  <p class="stat-percent">ทั้งหมด</p>
                </div>
                <v-avatar size="56" color="purple" class="stat-icon">
                  <v-icon dark size="28">mdi-counter</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear 
                :value="100" 
                color="purple" 
                height="4" 
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Section -->
      <v-row class="mb-6">
        <v-col cols="12" md="5">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <v-icon color="primary" class="mr-2">mdi-chart-donut</v-icon>
              สัดส่วนงบประมาณตามประเภท
            </v-card-title>
            <v-card-text>
              <apexchart
                ref="donutChart"
                type="donut"
                height="320"
                :options="donutChartOptions"
                :series="donutSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
              จำนวนตามประเภทงบประมาณ
            </v-card-title>
            <v-card-text>
              <apexchart
                ref="barChart"
                type="bar"
                height="320"
                :options="barChartOptions"
                :series="barSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Budget Table -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="data-table-card" elevation="4">
            <v-card-title class="table-title d-flex justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                <span>ข้อมูลประเภทงบประมาณ</span>
              </div>
              <div class="d-flex align-center table-actions">
                <v-btn
                  color="success"
                  class="mr-2"
                  @click="exportToExcel()"
                  :disabled="!budget_etc.length"
                >
                  <v-icon left>mdi-microsoft-excel</v-icon>
                  ส่งออก Excel
                </v-btn>               
              </div>
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหาข้อมูล..."
                outlined
                dense
                clearable
                hide-details
                class="mb-4 search-field"
              ></v-text-field>

              <v-data-table
                :loading="loading"
                :headers="headers"
                :items="budget_etc"
                :search="search"
                :items-per-page="10"
                :footer-props="{
                  'items-per-page-options': [5, 10, 15, 25, -1],
                  'items-per-page-text': 'แสดงรายการ:'
                }"
                class="modern-table"
                loading-text="กำลังโหลดข้อมูล..."
                no-data-text="ไม่พบข้อมูล"
                no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
              >
                <template v-slot:item.budget_etcdetail="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="36" :color="getBudgetColor(item.budget_etcdetail)" class="mr-3">
                      <v-icon dark small>{{ getBudgetIcon(item.budget_etcdetail) }}</v-icon>
                    </v-avatar>
                    <span class="budget-name">{{ item.budget_etcdetail }}</span>
                  </div>
                </template>

                <template v-slot:item.countBud="{ item }">
                  <v-chip color="primary" dark small class="font-weight-bold">
                    {{ formatNumber(item.countBud) }}
                  </v-chip>
                </template>

                <template v-slot:item.sumAll="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-circular
                      :value="calculatePercent(item)"
                      :color="getPercentColor(calculatePercent(item))"
                      size="40"
                      width="4"
                      class="mr-2"
                    >
                      <span class="percent-text">{{ calculatePercent(item) }}</span>
                    </v-progress-circular>
                    <span class="percent-label">%</span>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Sub Components -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-expansion-panels v-model="expandedPanel" multiple>
            <v-expansion-panel class="expansion-card">
              <v-expansion-panel-header class="expansion-header">
                <div class="d-flex align-center">
                  <v-icon color="green" class="mr-3">mdi-shield-check</v-icon>
                  <span class="expansion-title">สถานศึกษาปลอดภัย</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <Budget_etc_college_safety />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="expansion-card">
              <v-expansion-panel-header class="expansion-header">
                <div class="d-flex align-center">
                  <v-icon color="blue" class="mr-3">mdi-domain</v-icon>
                  <span class="expansion-title">ข้อมูลสถานศึกษา</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <Budget_etc_college />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="expansion-card">
              <v-expansion-panel-header class="expansion-header">
                <div class="d-flex align-center">
                  <v-icon color="orange" class="mr-3">mdi-clipboard-list</v-icon>
                  <span class="expansion-title">รายละเอียดสถานศึกษาปลอดภัย</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <Budget_etc_college_safety_de />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
          {{ snackbar.text }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import Budget_etc_college from "../../components/admin/budget_etc_college.vue";
import Budget_etc_college_safety from "../../components/admin/budget_etc_college_safety.vue";
import Budget_etc_college_safety_de from "../../components/admin/budget_etc_college_safety_de.vue";
import Manpower_bar from "../../components/admin/manpower_bar.vue";
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";

export default {
  name: "HRvecPersonnelEtcTem",

  components: {
    Budget_etc_college,
    Manpower_bar,
    Budget_etc_college_safety,
    Budget_etc_college_safety_de,
    apexchart: VueApexCharts
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      search: "",
      budget_etc: [],
      expandedPanel: [0],

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Table Headers
      headers: [
        { text: "ประเภทงบประมาณ", align: "left", value: "budget_etcdetail", width: "50%" },
        { text: "จำนวน", align: "center", value: "countBud", width: "25%" },
        { text: "ร้อยละ", align: "center", value: "sumAll", width: "25%" }
      ],

      // Chart Options
      donutChartOptions: {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        labels: [],
        colors: ["#2196F3", "#4CAF50", "#FF9800", "#9C27B0", "#F44336", "#00BCD4", "#795548", "#607D8B"],
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Sarabun, sans-serif",
          markers: {
            width: 12,
            height: 12,
            radius: 2
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "14px",
                  fontFamily: "Sarabun, sans-serif"
                },
                value: {
                  show: true,
                  fontSize: "20px",
                  fontFamily: "Sarabun, sans-serif",
                  fontWeight: "bold",
                  formatter: function(val) {
                    return parseInt(val).toLocaleString();
                  }
                },
                total: {
                  show: true,
                  label: "รวมทั้งหมด",
                  fontSize: "12px",
                  fontFamily: "Sarabun, sans-serif",
                  formatter: function(w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString();
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString() + " รายการ";
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: { width: 280 },
            legend: { position: "bottom" }
          }
        }]
      },

      barChartOptions: {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            barHeight: "60%",
            distributed: true
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "11px",
              fontFamily: "Sarabun, sans-serif"
            },
            formatter: function(val) {
              return parseInt(val).toLocaleString();
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: "11px",
              fontFamily: "Sarabun, sans-serif"
            },
            maxWidth: 200
          }
        },
        colors: ["#2196F3", "#4CAF50", "#FF9800", "#9C27B0", "#F44336", "#00BCD4", "#795548", "#607D8B"],
        legend: { show: false },
        fill: { opacity: 1 },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toLocaleString();
          },
          style: {
            fontSize: "11px",
            fontFamily: "Sarabun, sans-serif"
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val.toLocaleString() + " รายการ";
            }
          }
        }
      }
    };
  },

  computed: {
    statistics() {
      const totalBudget = this.budget_etc.length;
      const totalCount = this.budget_etc.reduce((sum, item) => sum + (parseInt(item.countBud) || 0), 0);
      const budgetTypes = this.budget_etc.length;
      const safetyCount = this.budget_etc.filter(item => 
        item.budget_etcdetail && item.budget_etcdetail.includes("ปลอดภัย")
      ).reduce((sum, item) => sum + (parseInt(item.countBud) || 0), 0);
      const safetyPercent = totalCount > 0 ? ((safetyCount / totalCount) * 100).toFixed(1) : 0;

      return {
        totalBudget,
        totalCount,
        budgetTypes,
        safetyCount,
        safetyPercent
      };
    },

    donutSeries() {
      return this.budget_etc.map(item => parseInt(item.countBud) || 0);
    },

    barSeries() {
      return [{
        name: "จำนวน",
        data: this.budget_etc.map(item => parseInt(item.countBud) || 0)
      }];
    }
  },

  watch: {
    budget_etc: {
      handler() {
        this.updateCharts();
      },
      deep: true
    }
  },

  async mounted() {
    await this.budget_etcQuery();
  },

  methods: {
    getCurrentYear() {
      return new Date().getFullYear() + 543;
    },

    formatNumber(num) {
      if (num === null || num === undefined || num === "") return "0";
      return Number(num).toLocaleString("th-TH");
    },

    calculatePercent(item) {
      if (!item.countBud || !item.sumAll || item.sumAll === 0) return 0;
      return parseFloat((item.countBud * 100) / item.sumAll).toFixed(2);
    },

    getPercentColor(percent) {
      if (percent >= 50) return "success";
      if (percent >= 25) return "warning";
      return "info";
    },

    getBudgetColor(detail) {
      if (!detail) return "grey";
      if (detail.includes("ปลอดภัย")) return "green";
      if (detail.includes("พัฒนา")) return "blue";
      if (detail.includes("บริหาร")) return "purple";
      if (detail.includes("วิชาการ")) return "orange";
      return "primary";
    },

    getBudgetIcon(detail) {
      if (!detail) return "mdi-help-circle";
      if (detail.includes("ปลอดภัย")) return "mdi-shield-check";
      if (detail.includes("พัฒนา")) return "mdi-trending-up";
      if (detail.includes("บริหาร")) return "mdi-account-tie";
      if (detail.includes("วิชาการ")) return "mdi-book-open-variant";
      if (detail.includes("อาคาร")) return "mdi-office-building";
      return "mdi-tag";
    },

    showSnackbar(color, icon, text) {
      this.snackbar = { show: true, color, icon, text, timeout: 5000 };
    },

    updateCharts() {
      const labels = this.budget_etc.map(item => 
        item.budget_etcdetail ? 
        (item.budget_etcdetail.length > 30 ? item.budget_etcdetail.substring(0, 30) + "..." : item.budget_etcdetail) 
        : "ไม่ระบุ"
      );

      if (this.$refs.donutChart) {
        this.$refs.donutChart.updateOptions({
          labels: labels
        });
      }

      if (this.$refs.barChart) {
        this.$refs.barChart.updateOptions({
          xaxis: {
            categories: labels
          }
        });
      }
    },

    async budget_etcQuery() {
      this.loading = true;
      try {
        let result = await this.$http.post("budget_etc.php", {
          ApiKey: this.ApiKey,
          budget_etcidSummary: "ok"
        });
        this.budget_etc = result.data || [];
        
        this.$nextTick(() => {
          this.updateCharts();
        });
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาดในการโหลดข้อมูล");
        console.error("Error loading budget data:", error);
      } finally {
        this.loading = false;
      }
    },

    // Export Methods
    exportToExcel() {
      const exportData = this.budget_etc.map(item => ({
        "ประเภทงบประมาณ": item.budget_etcdetail,
        "จำนวน": item.countBud,
        "ร้อยละ": this.calculatePercent(item) + "%"
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ข้อมูลประเภทสถานศึกษา");

      const colWidths = [
        { wch: 50 },
        { wch: 15 },
        { wch: 15 }
      ];
      ws["!cols"] = colWidths;

      XLSX.writeFile(wb, `ข้อมูลประเภทสถานศึกษา_${this.getCurrentYear()}.xlsx`);
      this.showSnackbar("success", "mdi-check-circle", "ส่งออกไฟล์ Excel สำเร็จ");
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap');

.budget-report {
  font-family: 'Sarabun', sans-serif;
  background: linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%);
  min-height: 100vh;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #43A047 0%, #1E88E5 100%);
  border-radius: 16px;
  padding: 24px 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(67, 160, 71, 0.3);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}

.header-badges {
  gap: 8px;
}

.header-chip {
  font-weight: 500;
}

/* Statistics Cards */
.stat-card {
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-blue { border-left-color: #2196F3; }
.stat-card-green { border-left-color: #4CAF50; }
.stat-card-orange { border-left-color: #FF9800; }
.stat-card-purple { border-left-color: #9C27B0; }

.stat-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.stat-percent {
  font-size: 0.8rem;
  color: #999;
  margin: 0;
}

.stat-icon {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Chart Cards */
.chart-card {
  border-radius: 16px;
  overflow: hidden;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

/* Data Table Card */
.data-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  padding: 16px 24px;
}

.table-actions {
  gap: 8px;
}

.search-field {
  max-width: 400px;
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
}

.modern-table >>> th {
  background: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  color: #333 !important;
}

.modern-table >>> td {
  font-size: 0.875rem;
  padding: 16px !important;
}

.modern-table >>> tr:hover {
  background: #e8f5e9 !important;
}

.budget-name {
  font-weight: 500;
  color: #333;
}

.percent-text {
  font-size: 0.7rem;
  font-weight: 600;
}

.percent-label {
  font-size: 0.875rem;
  color: #666;
}

/* Expansion Panels */
.expansion-card {
  border-radius: 12px !important;
  margin-bottom: 12px;
  overflow: hidden;
}

.expansion-header {
  background: #f8f9fa;
}

.expansion-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

/* Responsive */
@media (max-width: 960px) {
  .header-section {
    padding: 16px 20px;
  }
  
  .page-title {
    font-size: 1.25rem;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .table-actions {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card,
.chart-card,
.data-table-card {
  animation: fadeIn 0.5s ease forwards;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }
</style>